<template>
  <Transition name="modal-popup">
    <div v-if="store.state.open.editProfile" class="modal-mask" @click.self="handleClose">
      <div ref="cardRef" class="modal-card glass">
        <button class="modal-close close" @click="handleClose">×</button>
        <div class="header">
          <h2 class="title">修改资料</h2>
        </div>

        <!-- ========== 新增Tab区域 ========== -->
        <div class="tab-wrap">
          <button
              class="tab-item"
              :class="{active: activeTab === 'profile'}"
              @click="activeTab = 'profile'"
          >
            基础资料
          </button>
          <button
              class="tab-item"
              :class="{active: activeTab === 'passkey'}"
              @click="activeTab = 'passkey'"
          >
            通行密钥
          </button>
        </div>

        <!-- Tab1：基础资料【原有代码完全不动】 -->
        <div v-if="activeTab === 'profile'">
          <form @submit.prevent="handleSubmit" class="contact-form">
            <div class="form-row">
              <div class="form-item">
                <label>登录名</label>
                <input
                    v-model="form.username"
                    type="text"
                    placeholder="登录账号"
                    required
                />
              </div>
              <div class="form-item">
                <label>昵称</label>
                <input
                    v-model="form.nickname"
                    type="text"
                    placeholder="显示昵称"
                    required
                />
              </div>
            </div>

            <div class="form-row">
              <div class="form-item">
                <label>邮箱</label>
                <input
                    v-model="form.email"
                    type="email"
                    placeholder="邮箱地址"
                    required
                    disabled
                />
              </div>
              <div class="form-item">
                <label>密码</label>
                <input
                    v-model="form.password"
                    type="password"
                    placeholder="如需修改请输入新密码"
                />
              </div>
            </div>

            <div class="btn-wrap">
              <button class="submit-btn" :disabled="loading">
                {{ loading ? '保存中...' : '保存修改' }}
              </button>
            </div>
          </form>
        </div>

        <!-- Tab2：通行密钥管理 -->
        <div v-if="activeTab === 'passkey'" class="passkey-wrap">
          <p class="passkey-desc">通行密钥可替代密码，使用指纹/面容快速免密登录。</p>
          <div class="passkey-list" v-if="passkeyList.length > 0">
            <div class="passkey-item" v-for="item in passkeyList" :key="item.credentialId">
              <div class="passkey-info">
                <div class="name-line">
                  <div v-if="editingCredId !== item.credentialId" class="name">{{ item.deviceName }}</div>
                  <input
                      v-else
                      :ref="el => activeInputEl = el"
                      v-model="editingName"
                      class="name-input"
                      @keyup.enter="saveDeviceName()"
                      @blur="saveDeviceName()"
                      type="text"
                      maxlength="15"
                  />
                  <img v-if="editingCredId !== item.credentialId" src="@/assets/svg/edit.svg" alt="编辑" class="edit-name-btn" @click="startEditName(item)">
                </div>
                <div class="meta">创建：{{ item.createdAt }}｜最后登录：{{ item.updatedAt }}</div>
              </div>
              <button class="btn link-danger del-btn" @click="handleDeletePasskey(item.credentialId)">删除</button>
            </div>
          </div>
          <div v-else class="empty-tip">暂无绑定通行密钥</div>
          <div class="btn-wrap" style="margin-top:24px">
            <button class="submit-btn" @click="startPasskeyRegister">添加通行密钥</button>
          </div>
        </div>

        <div v-if="tip.show" class="tip" :class="{ success: tip.success, error: !tip.success }">
          {{ tip.text }}
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import {ref, reactive, defineEmits, watch, nextTick} from 'vue'
import {useStore} from "vuex";
import request from "@/api/request";
import showToast from "@/utils/toast";
import {base64urlToUint8Array,uint8ArrayToBase64url} from "@/utils/base64urlToUint8";
const store = useStore();
const emit = defineEmits(['success'])

const activeTab = ref('profile')
const passkeyList = ref([])

// 单次编辑：只支持一条编辑
const editingCredId = ref(null)
const editingName = ref('')
const activeInputEl = ref(null) // 存当前激活输入框DOM

// 进入编辑模式
async function startEditName(item) {
  editingCredId.value = item.credentialId
  editingName.value = item.deviceName || ''
  activeInputEl.value = null
  await nextTick()
  activeInputEl.value?.focus()
}

// 保存设备名称
async function saveDeviceName() {
  if (!editingCredId.value) return
  const credId = editingCredId.value
  const newName = editingName.value.trim() || ''
  try {
    const resp = await request.post('/v1/api/editDeviceName', {
      credentialId: credId,
      deviceName: newName
    })
    const res = resp.data
    if (res.responseCode === '0000') {
      showToast(res.responseDesc, 'success')
      await fetchPasskeyList()
    } else {
      showToast(res.responseDesc)
    }
  } catch (e) {
    showToast(e.message)
  } finally {
    // 退出编辑
    editingCredId.value = null
    editingName.value = ''
    activeInputEl.value = null
  }
}

// 获取通行密钥列表
async function fetchPasskeyList() {
  try {
    const response = await request.get(`/v1/api/queryPasskeyList?email=${store.state.userInfo.email}`)
    const res = response.data;
    if(res.responseCode === '0000'){
      passkeyList.value = res.data
    }else {
      showToast(res.responseDesc)
    }
  }catch(e){
    showToast(e.message)
  }
}
// 删除通行密钥
async function handleDeletePasskey(credId) {
  if(!confirm("确定删除这条通行密钥？删除后该设备无法再用此密钥登录")) return
  try {
    const response = await request.get(`/v1/api/deletePasskeyById?credentialId=${credId}`)
    const res = response.data;
    if(res.responseCode === '0000'){
      showToast(res.responseDesc, "success")
      await fetchPasskeyList()
    }else{
      showToast(res.responseDesc)
    }
  }catch(e){
    showToast(e.message)
  }
}

watch(() => store.state.open.editProfile, (newVal) => {
  if (newVal) {
    form.username = store.state.userInfo.username || ''
    form.nickname = store.state.userInfo.nickname || ''
    form.email = store.state.userInfo.email || ''
    form.password = null
    tip.show = false
    activeTab.value = 'profile'
    fetchPasskeyList()
    // 弹窗打开清空编辑状态
    editingCredId.value = null
    editingName.value = ''
    activeInputEl.value = null
  }
})

const cardRef = ref(null)
const loading = ref(false)
const form = reactive({
  username: '',
  nickname: '',
  email: '',
  password: null,
})
const tip = reactive({
  show: false,
  success: false,
  text: ''
})

const handleClose = () => {
  store.commit('open/editProfile', false)
  tip.show = false
  loading.value = false
  editingCredId.value = null
  editingName.value = ''
  activeInputEl.value = null
}

async function handleSubmit() {
  loading.value = true
  tip.show = false
  try {
    const response = await request.post("/v1/api/editProfile", {
      username: form.username,
      nickname: form.nickname,
      email: form.email,
      pd: form.password,
    })
    const resp = response.data;
    if (resp.responseCode === '0000') {
      tip.show = true
      tip.success = true
      await store.dispatch("userInfo/logout")
      tip.text = '资料修改成功,即将跳转登陆页面'
      emit('success');
      setTimeout(()=>{
        handleClose()
        location.href = '/login'
      }, 3000)
    } else {
      tip.show = true
      tip.success = false
      tip.text = resp.responseDesc
    }
  } catch (err) {
    tip.show = true
    tip.success = false
    tip.text = err.message
  } finally {
    loading.value = false
  }
}

async function startPasskeyRegister() {
  try {
    const response = await request.get(`/v1/api/registerStart?email=${store.state.userInfo.email}`);
    const resp = response.data
    if (resp.responseCode === '0000') {
      const data = resp.data
      const {redisKey, creationOpts} = data;
      const publicKey = creationOpts;

      if (publicKey.extensions && 'appidExclude' in publicKey.extensions) {
        delete publicKey.extensions.appidExclude;
        if (Object.keys(publicKey.extensions).length === 0) {
          delete publicKey.extensions;
        }
      }

      publicKey.challenge = base64urlToUint8Array(publicKey.challenge);
      publicKey.user.id = base64urlToUint8Array(publicKey.user.id);

      try {
        const credential = await navigator.credentials.create({publicKey});
        const attestationResponse = credential.response;

        const submitPayload = {
          redisKey,
          publicKeyCredential: JSON.stringify({
            id: credential.id,
            rawId: uint8ArrayToBase64url(credential.rawId),
            type: credential.type,
            response: {
              attestationObject: uint8ArrayToBase64url(attestationResponse.attestationObject),
              clientDataJSON: uint8ArrayToBase64url(attestationResponse.clientDataJSON),
              transports: attestationResponse.transports || []
            },
            clientExtensionResults: {}
          })
        };

        try {
          const finishResponse = await request.post("/v1/api/registerFinish", submitPayload);
          const finishResp = finishResponse.data;
          if (finishResp.responseCode === '0000') {
            showToast(finishResp.responseDesc,'success')
            await fetchPasskeyList()
          }else {
            showToast(finishResp.responseDesc)
          }
        } catch (e) {
          showToast(e.message)
        }
      } catch (err) {
        console.error("注册失败", err);
        showToast(err.message)
      }
    } else {
      showToast(resp.responseDesc)
    }
  } catch (e) {
    showToast(e.message)
  }
}
</script>

<style scoped>
.modal-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.25);
  z-index: 9999;
  padding: 20px;
  box-sizing: border-box;
}

.modal-card {
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%) scale(1);
  width: 100%;
  max-width: 720px;
  max-height: calc(100vh - 40px);
  overflow-y: auto;
  border-radius: 20px;
  padding: 36px 32px;
  box-sizing: border-box;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  transition: all 0.36s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.modal-popup-enter-from .modal-card {
  transform: translate(-50%, -120vh) scale(0.3);
  opacity: 0;
}

.modal-popup-enter-active .modal-card {
  transition: all 0.36s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.modal-popup-enter-to .modal-card {
  transform: translate(-50%, -50%) scale(1);
  opacity: 1;
}

.modal-popup-leave-from .modal-card {
  opacity: 1;
}

.modal-popup-leave-to .modal-card {
  transform: translate(-50%, -120vh) scale(0.3);
  opacity: 0;
}

.modal-popup-leave-active .modal-card {
  transition: all 0.24s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 18px;
  font-size: var(--laputa-fontsize-28);
}

.header {
  margin-bottom: 28px;
  text-align: center;
}

.title {
  font-size: var(--laputa-fontsize-28);
  font-weight: 700;
  margin: 0 0 8px;
  color: var(--laputa-common-font-color);
}

.tab-wrap {
  display:flex;
  gap:8px;
  margin-bottom:24px;
  border-bottom:1px solid var(--laputa-glass-header-separator);
  justify-content: center;
}
.tab-item {
  padding:10px 16px;
  background:transparent;
  border:none;
  font-size:var(--laputa-fontsize-15);
  cursor:pointer;
  color:var(--laputa-common-thirdary-font-color);
  position:relative;
}
.tab-item.active {
  color:var(--laputa-common-font-color);
  font-weight:500;
}
.tab-item.active::after{
  content:"";
  position:absolute;
  bottom:-1px;
  left:0;
  width:100%;
  height:2px;
  background:var(--laputa-primary);
}

.contact-form {
  width: 100%;
}

.form-row {
  display: flex;
  gap: 24px;
}

.form-item {
  flex: 1;
  margin-bottom: 20px;
}

.form-item label {
  display: block;
  font-size: var(--laputa-fontsize-14);
  color: var(--laputa-common-thirdary-font-color);
  margin-bottom: 8px;
}

.form-item input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid var(--laputa-glass-header-separator);
  border-radius: 20px;
  padding: 14px 18px;
  font-size: var(--laputa-fontsize-14);
  background: var(--laputa-glass-background);
  color: var(--laputa-common-thirdary-font-color);
  transition: 0.22s ease;
}

.form-item input:focus {
  outline: none;
  background: var(--laputa-body-background);
  border-color: var(--laputa-primary);
}

.btn-wrap {
  text-align: center;
  margin-top: 8px;
}

.submit-btn {
  width: 220px;
  padding: 14px;
  border-radius: 999px;
  border: none;
  background: var(--laputa-btn-background);
  color: var(--laputa-common-inverse-font-color);
  font-size: var(--laputa-fontsize-15);
  font-weight: 500;
  cursor: pointer;
  transition: 0.22s;
}

.submit-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.tip {
  margin-top: 16px;
  padding: 14px 16px;
  border-radius: 10px;
  font-size: var(--laputa-fontsize-14);
  text-align: center;
}

.tip.success {
  background: #ecfdf5;
  color: #047857;
}

.tip.error {
  background: #fef2f2;
  color: #b91c1c;
}

.passkey-wrap {
  text-align:center;
}
.passkey-desc {
  color:var(--laputa-common-thirdary-font-color);
  font-size:var(--laputa-fontsize-14);
  margin:0 0 16px;
  text-align:center;
}
.passkey-list {
  display:flex;
  flex-direction:column;
  gap:12px;
}
.passkey-item {
  display:flex;
  justify-content:space-between;
  align-items:center;
  padding:14px 18px;
  border:1px solid var(--laputa-glass-header-separator);
  border-radius:16px;
  background:var(--laputa-glass-background);
}
.passkey-info .name-line {
  display:flex;
  align-items:center;
  gap:10px;
}
.passkey-info .name {
  font-weight:500;
  font-size:var(--laputa-fontsize-14);
  color:var(--laputa-common-secondary-font-color);
}
.name-input {
  padding:4px 8px;
  border-radius:8px;
  border:1px solid var(--laputa-primary);
  background:var(--laputa-glass-background);
  color:var(--laputa-common-font-color);
  font-size:var(--laputa-fontsize-14);
  width: 180px;
}
.passkey-info .meta {
  font-size:var(--laputa-fontsize-12);
  color:var(--laputa-common-thirdary-font-color);
  margin-top:4px;
}
.del-btn {
  background:transparent;
  font-size:var(--laputa-fontsize-14);
}
.edit-name-btn {
  background:transparent;
  font-size:var(--laputa-fontsize-14);
  border: none;
  cursor: pointer;
}
.empty-tip {
  padding:20px;
  text-align:center;
  color:var(--laputa-common-thirdary-font-color);
}

@media (max-width: 992px) {
  .modal-card {
    padding: 28px 24px;
  }

  .title {
    font-size: 28px;
  }

  .form-row {
    gap: 16px;
  }
}

@media (max-width: 768px) {
  .modal-mask {
    padding: 12px;
  }

  .modal-card {
    padding: 24px 20px;
    border-radius: 14px;
    max-height: calc(100vh - 24px);
  }

  .title {
    font-size: 24px;
  }

  .header {
    margin-bottom: 20px;
  }

  .form-row {
    flex-direction: column;
    gap: 0;
  }

  .submit-btn {
    width: 100%;
  }
}
</style>
